import { Popover } from "antd";
import { useInstructorColor } from "context/InstructorColorProvider";
import {
  CSSProperties,
  KeyboardEvent,
  memo,
  MouseEvent,
  ReactNode,
  useEffect,
  useReducer,
  useState,
} from "react";
import { translate, translateWithVars } from "shared/languages";
import { CalendarColor, colors } from "shared/theme/color";
import { ClassCategory } from "shared/types/enum";
import { clientShortNames } from "shared/utils/clientNames";
import { tzDate } from "shared/utils/dayjs";
import {
  DETAIL_LINE_PX,
  BLOCK_PAD_Y,
  LINE_THRESHOLDS,
  MIN_BLOCK_PX,
  PX_PER_MIN,
  TITLE_LINE_PX,
} from "./constants";
import { EventHoverCard, hoverCardBodyStyle } from "./EventHoverCard";
import { PositionedEvent } from "./layout";
import { CalendarSchedule } from "./types";
import { useDraggableEvent } from "./useDraggableEvent";
import { formatToLocalDateRange } from "./utils";

// Shown while the instructor list is still loading: no colour at all, so
// nobody flashes a wrong one first.
const UNCOLOURED: CalendarColor = {
  bg: colors.card,
  bgPast: colors.card,
  accent: colors.lineStrong,
  text: colors.ink2,
  border: colors.line,
};

// How many lines the class name needs at the block's width (measured by the
// day column): 1 when it fits, 2 or 3 when it has to wrap.
export type TitleRows = 1 | 2 | 3;

// setTimeout's ceiling (~24.8 days). A visible week is never that far out.
const MAX_TIMER_MS = 2 ** 31 - 1;

// A memoized block no longer re-renders just because the page did, so it has
// to notice the clock itself: once when its class starts (it stops being
// draggable) and once when it ends (it turns to its past colours).
const useRerenderWhenPassed = (startMs: number, endMs: number) => {
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    const nowMs = Date.now();
    const timers = [startMs, endMs]
      .filter((t) => t > nowMs && t - nowMs < MAX_TIMER_MS)
      .map((t) => setTimeout(rerender, t - nowMs + 50));
    return () => timers.forEach(clearTimeout);
  }, [startMs, endMs]);
};

// Seats on an open class: "6/8", "8/8 Penuh · 2 Waitlist", the words in
// bold. Null for any other class, or when the payload has no counts. Shared
// with the pop-up blocks (PopupScheduleBlock), which never name an open
// class's clients either.
export const seatsLine = (event: CalendarSchedule): ReactNode => {
  if (event.class_category !== ClassCategory.Open) return null;
  if (event.capacity == null || event.num_of_bookings == null) return null;
  const full = event.num_of_bookings >= event.capacity;
  const waitlist = event.waitlist_count ?? 0;
  return (
    <>
      <span className="wc-num">
        {event.num_of_bookings}/{event.capacity}
      </span>
      {full && <strong> {translate("schedule.grid.full")}</strong>}
      {waitlist > 0 && (
        <>
          {" · "}
          <span className="wc-num">{waitlist}</span>{" "}
          <strong>{translate("schedule.grid.waitlist")}</strong>
        </>
      )}
    </>
  );
};

// The fourth line: seats for an open class, the clients for any other.
// Empty when the payload carries neither.
const extraLine = (event: CalendarSchedule): ReactNode =>
  event.class_category === ClassCategory.Open
    ? seatsLine(event)
    : clientsLine(event);

// A private class names its clients when they fit: one client in full, two
// or three by first name ("Gita, Hana"; "Ayu L., Ayu P." when two share
// one), more than that as a count
// ("5 Peserta"). The hover card has everyone's full name.
const MAX_NAMED_CLIENTS = 3;
const clientsLine = (event: CalendarSchedule): string | null => {
  const names = event.customer_names ?? [];
  if (names.length === 0) return null;
  if (names.length > MAX_NAMED_CLIENTS) {
    return translateWithVars("schedule.grid.participants_n", {
      n: names.length,
    });
  }
  if (names.length === 1) return names[0];
  return clientShortNames(names, event.customer_first_names).join(", ");
};

interface CalendarBlockProps<T extends CalendarSchedule> {
  block: PositionedEvent<T>;
  titleRows: TitleRows;
  // Whether "07:00–07:55" fits; otherwise the block shows "07:00".
  fullTime: boolean;
  draggable: boolean;
  onEventClick?: (e: T) => any | undefined;
}

const CalendarBlockInner = <T extends CalendarSchedule>({
  block,
  titleRows,
  fullTime,
  draggable,
  onEventClick,
}: CalendarBlockProps<T>) => {
  const { event } = block;
  const [popoverOpen, setPopoverOpen] = useState(false);
  const { getInstructorColorById } = useInstructorColor();
  useRerenderWhenPassed(block.startMs, block.endMs);

  const nowMs = Date.now();
  const past = nowMs >= block.endMs;
  // Past, in-progress or cancelled classes can't be moved by drag.
  const isDraggable = draggable && nowMs < block.startMs && !event.is_cancelled;
  const { dragProps, wasDragging } = useDraggableEvent({
    event,
    enabled: isDraggable,
    onStart: () => setPopoverOpen(false),
  });

  const colour =
    getInstructorColorById(event.instructor_id ?? event.staff_id) ?? UNCOLOURED;
  const className = event.class_name ?? event.title ?? "";
  const extra = extraLine(event);

  // What fits is decided by the block's own size, never by the class's length:
  // the name (wrapped only when it doesn't fit on one line), then the
  // instructor, the time and the seats or clients, each while there is height.
  const h = block.height;
  const lines: ReactNode[] = [];
  if (h < LINE_THRESHOLDS[0]) {
    lines.push(
      <span key="t" className="wc-title">
        {className}
        <span className="wc-inline"> · {event.instructor_name}</span>
      </span>
    );
  } else {
    // A wrapped name gives way to the instructor and the time: it takes fewer
    // rows (down to one, cut with "…") when the block is too short for all.
    let rows: number = titleRows;
    while (
      rows > 1 &&
      h < TITLE_LINE_PX * rows + DETAIL_LINE_PX * 2 + BLOCK_PAD_Y
    )
      rows -= 1;
    lines.push(
      <span
        key="t"
        className={rows > 1 ? `wc-title wc-clamp-${rows}` : "wc-title"}
      >
        {className}
      </span>
    );
    let used = BLOCK_PAD_Y + TITLE_LINE_PX * rows;
    const detail = (key: string, node: ReactNode, numeric = false) => {
      if (used + DETAIL_LINE_PX > h) return;
      used += DETAIL_LINE_PX;
      lines.push(
        <span key={key} className={numeric ? "wc-line wc-num" : "wc-line"}>
          {node}
        </span>
      );
    };
    detail("i", event.instructor_name);
    detail("time", fullTime ? block.timeLabel : block.startLabel, true);
    if (extra) detail("x", extra);
  }

  const open = () => {
    if (wasDragging()) return;
    onEventClick?.(event);
  };
  const onClick = (e: MouseEvent<HTMLDivElement>) => {
    // Don't let a block click reach the column's create handler, and ignore
    // the synthetic click that follows a drop.
    e.stopPropagation();
    open();
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      open();
    }
  };

  const style = {
    top: block.top,
    height: block.height,
    left: block.left,
    width: block.width,
    "--wc-bg": past ? colour.bgPast : colour.bg,
    "--wc-accent": colour.accent,
    "--wc-text": colour.text,
  } as CSSProperties;

  return (
    <Popover
      open={popoverOpen}
      onOpenChange={(next) => {
        // Block re-opening mid-drag (the pointer re-enters as the ghost moves).
        if (wasDragging() && next) return;
        setPopoverOpen(next);
      }}
      mouseEnterDelay={0.4}
      arrow={false}
      styles={{ body: hoverCardBodyStyle }}
      content={<EventHoverCard event={event} dotColor={colour.accent} />}
    >
      <div
        className="wc-block"
        data-past={past || undefined}
        data-cancelled={event.is_cancelled || undefined}
        data-draggable={isDraggable || undefined}
        data-need-action={event.need_action || undefined}
        role="button"
        tabIndex={0}
        aria-label={`${className}, ${event.instructor_name}, ${block.timeLabel}`}
        style={style}
        onClick={onClick}
        onKeyDown={onKeyDown}
        {...dragProps}
      >
        <span className="wc-bar" aria-hidden />
        {/* Attendance still to mark: a dot in the corner, matching the day
            header's "N Aksi" chip (top-right too), so it never takes width
            from the class name. */}
        {event.need_action && <span className="wc-dot" aria-hidden />}
        {lines}
      </div>
    </Popover>
  );
};

// Memoized on the block's layout object (rebuilt only when the week's data
// changes), what fits at its width and the handlers, so a draft preview moving or the
// grid widening re-renders no block.
export const CalendarBlock = memo(
  CalendarBlockInner
) as typeof CalendarBlockInner;

/**
 * The create panel's preview of a class it is about to schedule: an outline
 * in ink on the card colour, the one treatment no real class ever has, so it
 * stays unmistakable whichever instructor is picked. Not interactive.
 */
export const GhostBlock = ({ event }: { event: CalendarSchedule }) => {
  const startMs = new Date(event.start_time).getTime();
  const endMs = new Date(event.end_time).getTime();
  const start = tzDate(event.start_time);
  const startMin = start.hour() * 60 + start.minute();
  const top = startMin * PX_PER_MIN + 1;
  const height =
    Math.max(((endMs - startMs) / 60000) * PX_PER_MIN, MIN_BLOCK_PX) - 2;
  return (
    <div className="wc-ghost" style={{ top, height }} aria-hidden>
      <span className="wc-title">{event.class_name ?? event.title}</span>
      {height >= LINE_THRESHOLDS[0] && (
        <span className="wc-line wc-num">
          {formatToLocalDateRange(event.start_time, event.end_time)}
        </span>
      )}
    </div>
  );
};
